import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import {
  SUPPORTED, LANG_STORAGE_KEY, langFromLocale, localeFromLang,
  translate, detectBrowserLang,
} from '@/i18n';
import phrasesEn from '@/i18n/phrasesEn';
import phrasesDe from '@/i18n/phrasesDe';
import phrasesPt from '@/i18n/phrasesPt';
import { setPhraseTranslator } from '@/i18n/reactPatch';

// side-effect: patch React.createElement once
import '@/i18n/reactPatch';

const LocaleCtx = createContext(null);
const MANUAL_KEY = `${LANG_STORAGE_KEY}_manual`;

const PHRASES = {
  es: null,
  en: phrasesEn,
  de: phrasesDe,
  pt: phrasesPt,
};

function readStoredLang() {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && ['es', 'en', 'de', 'pt'].includes(saved)) return saved;
  } catch { /* ignore */ }
  return null;
}

function phraseLookup(lang, text) {
  if (!text || typeof text !== 'string') return text;
  if (lang === 'es') return text;
  const dict = PHRASES[lang];
  if (!dict) return text;
  const hit = (s) => (s && dict[s] != null ? dict[s] : null);
  let found = hit(text);
  if (found != null) return found;
  const trimmed = text.trim();
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  const wrap = (v) => (trimmed === text ? v : lead + v + trail);
  found = hit(trimmed);
  if (found != null) return wrap(found);
  const ellipsis = trimmed.replace(/\.{3}/g, '…');
  found = hit(ellipsis) || hit(trimmed.replace(/…/g, '...'));
  if (found != null) return wrap(found);
  const noEnd = trimmed.replace(/[.:…]+$/, '');
  found = hit(noEnd);
  if (found != null) return wrap(found);
  return text;
}

export function LocaleProvider({ children, user }) {
  const [lang, setLangState] = useState(() => readStoredLang() || detectBrowserLang());
  const [source, setSource] = useState(() => {
    try {
      if (localStorage.getItem(MANUAL_KEY) === '1') return 'manual';
    } catch { /* ignore */ }
    return 'local';
  });

  // Install runtime phrase translator for createElement patch
  useEffect(() => {
    setPhraseTranslator((s) => phraseLookup(lang, s));
    return () => setPhraseTranslator((s) => s);
  }, [lang]);

  useEffect(() => {
    if (!user?.locale) return;
    let manual = false;
    try { manual = localStorage.getItem(MANUAL_KEY) === '1'; } catch { /* ignore */ }
    if (manual) {
      const current = readStoredLang() || lang;
      const userLang = langFromLocale(user.locale);
      if (current && current !== userLang) {
        api.patch('/auth/me/locale', { language: current }).catch(() => {});
      }
      return;
    }
    const l = langFromLocale(user.locale);
    setLangState(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch { /* ignore */ }
    setSource('user');
  }, [user?.locale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let manual = false;
        try { manual = localStorage.getItem(MANUAL_KEY) === '1'; } catch { /* ignore */ }
        if (manual || user?.locale) return;
        const r = await api.get('/auth/locale-suggest');
        if (cancelled) return;
        const l = r.data?.language;
        if (l && ['es', 'en', 'de', 'pt'].includes(l)) {
          setLangState(l);
          try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch { /* ignore */ }
          setSource(r.data?.source || 'suggest');
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.locale]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.setAttribute('data-lang', lang);
  }, [lang]);

  const setLang = useCallback(async (code, { persistUser = true } = {}) => {
    if (!['es', 'en', 'de', 'pt'].includes(code)) return;
    setLangState(code);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
      localStorage.setItem(MANUAL_KEY, '1');
    } catch { /* ignore */ }
    setSource('manual');
    if (persistUser && user?.id) {
      try {
        await api.patch('/auth/me/locale', { language: code });
      } catch { /* offline ok */ }
    }
  }, [user?.id]);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  const tp = useCallback((text) => phraseLookup(lang, text), [lang]);

  const value = useMemo(() => ({
    lang,
    locale: localeFromLang(lang),
    setLang,
    t,
    tp,
    supported: SUPPORTED,
    source,
  }), [lang, setLang, t, tp, source]);

  // key={lang} forces full remount so createElement re-runs with new phrases
  return (
    <LocaleCtx.Provider value={value}>
      <LangBoundary key={`cav-lang-${lang}`} lang={lang}>
        {children}
      </LangBoundary>
    </LocaleCtx.Provider>
  );
}

function LangBoundary({ children }) {
  return children;
}

export function useLocale() {
  const ctx = useContext(LocaleCtx);
  if (!ctx) {
    return {
      lang: 'es',
      locale: 'es-MX',
      setLang: () => {},
      t: (k) => translate('es', k),
      tp: (s) => s,
      supported: SUPPORTED,
      source: 'fallback',
    };
  }
  return ctx;
}

export function LanguageSelect({ className = '', compact = false }) {
  const { lang, setLang, supported, t } = useLocale();
  return (
    <label className={`inline-flex items-center gap-1.5 text-xs text-[#64748B] ${className}`} data-testid="language-select-wrap">
      {!compact && <span className="font-medium whitespace-nowrap">{t('lang.choose')}</span>}
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        className="border border-[#E2E8F0] rounded-lg bg-white px-2 py-1.5 text-xs text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-[#F72585] min-w-[7.5rem]"
        data-testid="language-select"
        aria-label={t('lang.choose')}
      >
        {supported.map((s) => (
          <option key={s.code} value={s.code}>{s.native}</option>
        ))}
      </select>
    </label>
  );
}
